import React from 'react';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Brain, Search, Newspaper, Building, Users, MessageSquare, Sparkles, CheckCircle, Loader, AlertCircle } from 'lucide-react';

const agentIcons = {
  market_tracker: Search,
  sentiment: Brain,
  company_news: Newspaper,
  company_info: Building,
  social_sentiment: Users,
  conversation_analyzer: MessageSquare,
  master_analyst: Sparkles,
};

const agentDisplayNames = {
  market_tracker: 'Market Tracker',
  sentiment: 'Sentiment',
  company_news: 'Company News',
  company_info: 'Company Intel',
  social_sentiment: 'Social Sentiment',
  conversation_analyzer: 'Deep Analyzer',
  master_analyst: 'Master Analyst',
};

export const AgentActivityFeed = ({ agents, isAnalyzing, lastAnalysis }) => {
  const statuses = agents?.statuses || {};

  const getStatusBadge = (status) => {
    switch (status) {
      case 'running':
        return (
          <Badge variant="outline" className="text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30 text-[10px] gap-1">
            <Loader className="w-2.5 h-2.5 animate-spin" />
            Running
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 text-[10px] gap-1">
            <CheckCircle className="w-2.5 h-2.5" />
            Done
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="text-red-400 border-red-500/30 text-[10px] gap-1">
            <AlertCircle className="w-2.5 h-2.5" />
            Error
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))] text-[10px]">
            Idle
          </Badge>
        );
    }
  };

  const agentEntries = Object.keys(agentIcons).map((key) => ({
    key,
    ...statuses[key],
  }));

  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4" data-testid="agent-activity-feed">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold tracking-wide uppercase text-[hsl(var(--muted-foreground))]">
          Agent Activity
        </h3>
        {isAnalyzing && (
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[hsl(var(--primary))] animate-pulse" />
            <span className="text-xs text-[hsl(var(--primary))] font-medium">Analyzing</span>
          </div>
        )}
      </div>

      <ScrollArea className="h-[360px]">
        <div className="space-y-2 pr-2">
          {agentEntries.map((agent) => {
            const Icon = agentIcons[agent.key] || Brain;
            const isRunning = agent.status === 'running';
            return (
              <div
                key={agent.key}
                className={`p-3 rounded-lg border border-[hsl(var(--border))] transition-colors duration-150 ${
                  isRunning ? 'bg-[hsl(var(--accent))] agent-shimmer' : 'bg-[hsl(var(--surface-2))]'
                }`}
                data-testid="agent-activity-item"
              >
                <div className="flex items-center justify-between mb-1.5 relative z-10">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${isRunning ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--muted-foreground))]'}`} />
                    <span className="text-sm font-medium">{agentDisplayNames[agent.key]}</span>
                  </div>
                  {getStatusBadge(agent.status)}
                </div>
                {agent.summary && (
                  <p className="text-xs text-[hsl(var(--muted-foreground))] ml-6 relative z-10">
                    {agent.summary}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {lastAnalysis && (
        <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Last analysis: {new Date(lastAnalysis).toLocaleTimeString()}
          </p>
        </div>
      )}
    </div>
  );
};
